/**
 * CoWork store — the desktop-only "which local folder is the agent's hands"
 * state (hybrid CoWork, Model C). When a `deviceId` is set here, chat turns
 * carry it in the request body and the hosted agent runs its workspace ops on
 * the user's machine (a DeviceWorkspace on the server).
 *
 * @module store/cowork-store
 */

import { create } from "zustand";

import type { ApprovalRequest, Autonomy, ExecTier, WorkspaceChange } from "@/lib/desktop";

/** Which surface the desktop user is in. Web is always implicitly "chat". */
export type CoworkMode = "chat" | "cowork";

// CoWork task history: which chats ran in CoWork. There's no server-side tag,
// so we remember the ids locally (desktop only) and filter the shared history.
const COWORK_CHATS_KEY = "taqat_cowork_chat_ids";
function loadCoworkChatIds(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(COWORK_CHATS_KEY) || "[]");
    return Array.isArray(raw) ? raw.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}
function persistCoworkChatIds(ids: string[]): void {
  try {
    localStorage.setItem(COWORK_CHATS_KEY, JSON.stringify(ids));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

// Per-chat folder + activity, so reopening a CoWork chat restores its workspace.
const CHAT_FOLDERS_KEY = "taqat_cowork_chat_folders";
const CHAT_ACTIVITY_KEY = "taqat_cowork_chat_activity";
function loadMap<T>(key: string): Record<string, T> {
  try {
    const raw = JSON.parse(localStorage.getItem(key) || "{}");
    return raw && typeof raw === "object" ? (raw as Record<string, T>) : {};
  } catch {
    return {};
  }
}
function persistMap<T>(key: string, m: Record<string, T>): void {
  try {
    localStorage.setItem(key, JSON.stringify(m));
  } catch {
    /* ignore */
  }
}

interface CoworkState {
  /** Chat vs CoWork (the sidebar tab). Only "cowork" routes turns to the device. */
  mode: CoworkMode;
  /** Connected desktop device id, or null when not in CoWork mode. */
  deviceId: string | null;
  /** Basename of the opened folder, for display. */
  folderName: string | null;
  /** Absolute path (desktop-only; never sent to the server). */
  folderPath: string | null;
  /** Whether the device socket is currently live. */
  connected: boolean;
  /** How autonomously the agent may act on this folder. */
  autonomy: Autonomy;
  /** Where commands run (native host vs WSL2). */
  execTier: ExecTier;
  /** Changes made this session (for the Changes panel). */
  changes: WorkspaceChange[];
  /**
   * Pending mutation approvals, FIFO. Held in the store (not component state) so
   * a re-render/remount can never drop a prompt while the write hangs waiting.
   */
  approvals: ApprovalRequest[];
  /** Ids of chats that ran in CoWork (for the CoWork task history). */
  coworkChatIds: string[];
  /** Whether the right Context panel is collapsed to a rail (CoWork only). */
  contextPanelCollapsed: boolean;
  /** Reconnecting the device to a chat's folder (drives loading UI). */
  reconnecting: boolean;
  setMode: (mode: CoworkMode) => void;
  markCoworkChat: (chatId: string) => void;
  toggleContextPanel: () => void;
  setReconnecting: (v: boolean) => void;
  /** Remember which folder a chat worked in (for auto-reconnect on return). */
  rememberChatFolder: (chatId: string, folderPath: string) => void;
  getChatFolder: (chatId: string) => string | undefined;
  /** Persist / restore a chat's activity feed across sessions. */
  saveChatActivity: (chatId: string, changes: WorkspaceChange[]) => void;
  restoreChatActivity: (chatId: string) => void;
  setSession: (session: { deviceId: string; folderName: string; folderPath?: string }) => void;
  setConnected: (connected: boolean) => void;
  setAutonomy: (autonomy: Autonomy) => void;
  setExecTier: (execTier: ExecTier) => void;
  setChanges: (changes: WorkspaceChange[]) => void;
  enqueueApproval: (req: ApprovalRequest) => void;
  dequeueApproval: (id: string) => void;
  clear: () => void;
}

export const useCoworkStore = create<CoworkState>((set) => ({
  mode: "chat",
  deviceId: null,
  folderName: null,
  folderPath: null,
  connected: false,
  autonomy: "ask",
  execTier: "native",
  changes: [],
  approvals: [],
  coworkChatIds: loadCoworkChatIds(),
  contextPanelCollapsed: false,
  reconnecting: false,

  setMode: (mode) => set({ mode }),
  setReconnecting: (v) => set({ reconnecting: v }),
  toggleContextPanel: () => set((s) => ({ contextPanelCollapsed: !s.contextPanelCollapsed })),
  markCoworkChat: (chatId) =>
    set((s) => {
      if (!chatId || s.coworkChatIds.includes(chatId)) return s;
      const next = [chatId, ...s.coworkChatIds].slice(0, 300);
      persistCoworkChatIds(next);
      return { coworkChatIds: next };
    }),
  rememberChatFolder: (chatId, folderPath) => {
    if (!chatId || !folderPath) return;
    const m = loadMap<string>(CHAT_FOLDERS_KEY);
    if (m[chatId] === folderPath) return;
    m[chatId] = folderPath;
    persistMap(CHAT_FOLDERS_KEY, m);
  },
  getChatFolder: (chatId) => (chatId ? loadMap<string>(CHAT_FOLDERS_KEY)[chatId] : undefined),
  saveChatActivity: (chatId, changes) => {
    if (!chatId) return;
    const m = loadMap<WorkspaceChange[]>(CHAT_ACTIVITY_KEY);
    m[chatId] = changes.slice(-200);
    persistMap(CHAT_ACTIVITY_KEY, m);
  },
  restoreChatActivity: (chatId) =>
    set({ changes: (chatId ? loadMap<WorkspaceChange[]>(CHAT_ACTIVITY_KEY)[chatId] : undefined) ?? [] }),
  setSession: ({ deviceId, folderName, folderPath }) =>
    set({ deviceId, folderName, folderPath: folderPath ?? null }),
  setConnected: (connected) => set({ connected }),
  setAutonomy: (autonomy) => set({ autonomy }),
  setExecTier: (execTier) => set({ execTier }),
  setChanges: (changes) => set({ changes }),
  enqueueApproval: (req) =>
    set((s) => (s.approvals.some((r) => r.id === req.id) ? s : { approvals: [...s.approvals, req] })),
  dequeueApproval: (id) => set((s) => ({ approvals: s.approvals.filter((r) => r.id !== id) })),
  clear: () =>
    // NOTE: the step list is NOT cleared here — it moved to `plan-store` in
    // spec 084 step 1 precisely because it outlives the CoWork session.
    set({ deviceId: null, folderName: null, folderPath: null, connected: false, changes: [], approvals: [] }),
}));
